import type * as THREE from 'three/webgpu';
import type {SketchLineObject} from '../../../../mesh/sketch/SketchLineObject';
import type {SketchPointObject} from '../../../../mesh/sketch/SketchPointObject';
import type {SketchCircleObject} from '../../../../mesh/sketch/SketchCircleObject';
import type {SketchArcObject} from '../../../../mesh/sketch/SketchArcObject';
import type {SketchEllipseObject} from '../../../../mesh/sketch/SketchEllipseObject';
import {isSketchLineObject} from '../../../../mesh/guards/isSketchLineObject';
import {isSketchPointObject} from '../../../../mesh/guards/isSketchPointObject';
import {isSketchCircleObject} from '../../../../mesh/guards/isSketchCircleObject';
import {isSketchArcObject} from '../../../../mesh/guards/isSketchArcObject';
import {isSketchEllipseObject} from '../../../../mesh/guards/isSketchEllipseObject';
import {isSketchParabolaObject} from '../../../../mesh/guards/isSketchParabolaObject';
import {isSketchSplineObject} from '../../../../mesh/guards/isSketchSplineObject';
import {isSketchStyleSplineObject} from '../../../../mesh/guards/isSketchStyleSplineObject';
import {isMirrorAxisGeometryProvider} from '../../../../mesh/guards/isMirrorAxisGeometryProvider';
import {isSketchObject} from '../../../../mesh/guards/isSketchObject';
import type {ISketchObject} from '../../../../mesh/sketch/ISketchObject';
import {
    getSketchRelationCategory,
    isCreatableSketchRelationKind,
    SketchRelationCategory,
    SketchRelationKind,
} from './SketchRelationKinds';
import type {SketchRelationDefinition} from '../relationDefinition/SketchRelationDefinition';
import {
    resolveSlotRelationConstraintClassMap,
    resolveArcRelationConstraintClassMap,
    resolveCircleRelationConstraintClassMap,
    resolveLineRelationConstraintClassMap,
    resolvePointRelationConstraintClassMap,
} from '../GeometricRelationConstraints';
import {
    PointCurveCoincidentConstraint,
    PointMidpointConstraint,
    SketchEntitySymmetricConstraint,
    TangentConstraint,
} from '../relationDefinition';
import {beginSketchSolverHistorySession} from '../solverHistory';
import type {
    AvailableRelationButtonViewModel,
    CoincidentCurveObject,
    GeometryRelationBatchCreationParams,
    GeometryRelationAttachment,
    GeometryRelationCreationParams,
    GeometryRelationReplacementParams,
    SketchRelationCreationViewerPort,
    TangentCurveObject,
    TangentLineLikeObject,
} from '../../../../../types/sketch/solver/constraint/SketchRelationCreationService';

export type {
    AvailableRelationButtonViewModel,
    GeometryRelationBatchCreationParams,
    GeometryRelationAttachment,
    GeometryRelationCreationParams,
    GeometryRelationReplacementParams,
    SketchActiveParametricStateSolveInput,
    SketchRelationCreationHistoryMode,
} from '../../../../../types/sketch/solver/constraint/SketchRelationCreationService';

/**
 * 几何关系创建服务。
 * 职责：
 * 1. 根据关系类型判断创建路径（self / multi）；
 * 2. 解析约束类型映射并创建约束实例；
 * 3. 将单条、整批或替换候选关系绑定到目标对象，并通过注入端口触发统一参数求解；
 * 4. 在创建、替换或求解失败时原子回滚本次关系变更。
 */
export class SketchRelationCreationService {
    /** 持有本服务的 Viewer 求解端口。 */
    private readonly viewer: SketchRelationCreationViewerPort;

    readonly isGeometryRelationCreationService: boolean = true;

    /**
     * 创建几何关系服务。
     *
     * @param viewer 持有本服务的 Viewer；服务通过该端口访问活动草图参数求解与定义提交。
     */
    public constructor(viewer: SketchRelationCreationViewerPort) {
        this.viewer = viewer;
    }
    /**
     * 根据输入参数创建几何关系。
     *
     * @param params 创建参数。
     * @returns 是否至少创建成功一条关系。
     */
    public createRelation(params: GeometryRelationCreationParams): boolean {
        return this.createRelations({
            relations: [{
                relationKind: params.relationKind,
                objects: params.objects,
            }],
            ...(params.historyMode ? {historyMode: params.historyMode} : {}),
        });
    }

    /**
     * 在同一求解与回滚边界内批量创建几何关系。
     *
     * 说明：
     * 1. 复合草图命令会一次生成多条结构关系，逐条求解会暴露不完整中间状态；
     * 2. 本入口先绑定整批候选，再执行一次统一参数求解；
     * 3. 任一候选无法创建或整批求解失败时，全部恢复到创建前状态。
     *
     * @param params 批量创建参数。
     * @returns 整批关系全部创建并求解成功时返回 true。
     */
    public createRelations(params: GeometryRelationBatchCreationParams): boolean {
        if (params.relations.length === 0) {
            return false;
        }

        const unsupportedRelation = params.relations.find((relation) => {
            return !isCreatableSketchRelationKind(relation.relationKind);
        });
        if (unsupportedRelation) {
            console.warn(`关系 ${unsupportedRelation.relationKind} 尚未具备生产创建闭环，禁止创建。`);
            return false;
        }

        const seedObjects = this.collectUniqueRelationObjects(params.relations);
        const relationIdsBefore = this.captureRelationIds(seedObjects);
        /**
         * 无论历史由谁提交，都先捕获局部 solver before，保证候选求解或定义提交失败时可完整回滚。
         * `parent-managed` 仅在成功后丢弃独立记录，由外层 SketchGroup 事务捕获最终状态。
         */
        const historySession = beginSketchSolverHistorySession({
            source: 'relation-added',
            seedObjects,
            automaticRelation: true,
        });
        /**
         * 恢复关系创建前状态。
         *
         * 运行时关系 ID 快照负责无历史环境下的精确删除，历史会话负责恢复可能已经
         * 写回的几何状态；两个补偿步骤必须归属于同一个创建事务。
         */
        const rollbackCreation = (): void => {
            this.rollbackCreatedRelations(seedObjects, relationIdsBefore);
            historySession?.rollback({seedObjects});
        };

        try {
            const created = params.relations.every((relation) => {
                return this.createRelationDefinition(relation);
            });

            if (created) {
                const singleRelationKind = params.relations.length === 1
                    ? params.relations[0]?.relationKind
                    : undefined;
                if (!this.solveActiveSketchParametricRelations(seedObjects, singleRelationKind)) {
                    rollbackCreation();
                    return false;
                }
                if (params.historyMode === 'parent-managed') {
                    // 外层绘制事务负责 after；局部会话只承担失败补偿，不得再输出独立 solver 历史。
                    historySession?.discard();
                } else {
                    const commitPromise = historySession?.commit({seedObjects});

                    /**
                     * 关系创建入口保持 SolidWorks 风格的同步返回；历史端口可能需要等待 IndexedDB/OPFS。
                     * 异步失败时由 solver history session 内部执行 before rollback，这里只负责避免未处理 Promise。
                     */
                    void commitPromise?.catch((error) => {
                        (globalThis as { reportError?: (reportedError: unknown) => void }).reportError?.(error);
                    });
                }
            } else {
                // 前序候选可能已经绑定，任一候选失败都必须撤销整批定义，保持批量入口原子性。
                rollbackCreation();
            }

            return created;
        } catch (error) {
            // 约束创建、提交前收口或历史写入失败时，都必须尽量回到 before，避免模型变更没有 undo 记录。
            rollbackCreation();
            const relationKinds = params.relations.map((relation) => relation.relationKind).join(', ');
            console.warn(`关系 ${relationKinds} 创建失败，已恢复创建前状态。`, error);
            return false;
        }
    }

    /**
     * 在调用方已经持有的模型事务内原子替换一组几何关系。
     *
     * 该入口服务于多边形、阵列等“实体创建或属性重建时自动生成关系”的领域操作；
     * 支持绑定领域服务预先构造的带参关系，并负责旧关系移除、候选关系批量绑定、
     * 单次统一参数求解和失败时的关系定义回滚，不再额外创建独立的关系历史步骤。
     *
     * @param params 旧关系、创建请求和当前有效求解种子。
     * @returns 关系替换与统一参数求解均成功时返回 true。
     */
    public replaceRelationsWithinActiveMutation(params: GeometryRelationReplacementParams): boolean {
        const relationsToRemove = Array.from(new Map(
            params.relationsToRemove.map((relation) => [relation.id, relation] as const),
        ).values());
        const removedRelationTargets = new Map<SketchRelationDefinition, readonly ISketchObject[]>();
        const attachments = params.attachments ?? [];

        // 保存旧关系的全部直接目标，任何失败都必须恢复到相同对象容器。
        relationsToRemove.forEach((relation) => {
            removedRelationTargets.set(
                relation,
                relation.getTargetsSnapshot() as readonly ISketchObject[],
            );
        });

        const allMutationObjects = Array.from(new Map(
            [
                ...params.seedObjects,
                ...params.creations.flatMap((creation) => creation.objects),
                ...attachments.flatMap((attachment) => (
                    attachment.targets.map((target) => target as unknown as THREE.Object3D)
                )),
                ...Array.from(removedRelationTargets.values())
                    .flat()
                    .map((target) => target as unknown as THREE.Object3D),
            ].map((object) => [object.uuid, object] as const),
        ).values());
        const relationIdsBefore = this.captureRelationIds(allMutationObjects);

        /**
         * 删除本次新建关系，并把替换前的旧关系重新挂回全部原始目标。
         */
        const rollbackReplacement = (): void => {
            this.rollbackCreatedRelations(allMutationObjects, relationIdsBefore);
            removedRelationTargets.forEach((targets, relation) => {
                targets.forEach((target) => {
                    target.constraints.addRelation(
                        relation as Parameters<typeof target.constraints.addRelation>[0],
                    );
                });
            });
        };

        try {
            // 关系必须从所有直接目标同时删除，禁止留下单侧悬空定义。
            removedRelationTargets.forEach((targets, relation) => {
                targets.forEach((target) => {
                    target.constraints.removeRelation(relation.id);
                });
            });

            // 带参结构关系由领域服务构造，统一替换边界只负责完整绑定与回滚。
            for (const attachment of attachments) {
                if (attachment.targets.length === 0) {
                    rollbackReplacement();
                    return false;
                }
                attachment.targets.forEach((target) => {
                    target.constraints.addRelation(
                        attachment.relation as Parameters<typeof target.constraints.addRelation>[0],
                    );
                });
            }

            for (const creation of params.creations) {
                if (!isCreatableSketchRelationKind(creation.relationKind)) {
                    rollbackReplacement();
                    return false;
                }
                if (!this.createRelationDefinition(creation)) {
                    rollbackReplacement();
                    return false;
                }
            }

            if (relationsToRemove.length === 0 && params.creations.length === 0 && attachments.length === 0) {
                return true;
            }
            if (!this.solveActiveSketchParametricRelations(
                params.seedObjects,
                undefined,
                'parent-managed',
            )) {
                rollbackReplacement();
                return false;
            }
            return true;
        } catch (error) {
            rollbackReplacement();
            console.warn('草图关系批量替换失败，已恢复替换前关系定义。', error);
            return false;
        }
    }

    /**
     * 绑定一条候选关系定义，不执行求解或历史提交。
     * @param params 单条关系创建参数。
     * @returns 候选定义成功绑定到目标对象时返回 true。
     */
    private createRelationDefinition(
        params: Omit<GeometryRelationCreationParams, 'historyMode'>,
    ): boolean {
        const category = getSketchRelationCategory(params.relationKind);
        // 槽口专属关系先行分派：目标是槽口实体本身，不走线/点/圆的按类型映射。
        const slotCreated = this.createSlotRelation(params);
        return slotCreated || (category === SketchRelationCategory.Self
            ? this.createSelfRelation(params)
            : this.createMultiRelation(params));
    }

    /**
     * 收集整批关系涉及的唯一目标对象。
     * @param relations 批量关系定义。
     * @returns 按首次出现顺序排列的唯一目标对象。
     */
    private collectUniqueRelationObjects(
        relations: readonly Omit<GeometryRelationCreationParams, 'historyMode'>[],
    ): THREE.Object3D[] {
        const objectMap = new Map<string, THREE.Object3D>();
        relations.forEach((relation) => {
            relation.objects.forEach((object) => {
                if (!objectMap.has(object.uuid)) {
                    objectMap.set(object.uuid, object);
                }
            });
        });
        return Array.from(objectMap.values());
    }

    /**
     * 对当前候选关系直接目标执行统一参数求解并提交草图定义。
     *
     * 该入口属于关系创建服务内部事务：直接目标只用于扩展当前关系连通岛，Viewer
     * 负责装配活动草图运行时上下文；求解成功后再提交一次定义变更，失败时由
     * `createRelation` 的统一回滚分支撤销候选关系。
     *
     * @param seedObjects 本次关系直接涉及的草图对象；省略时验证活动草图全部分量。
     * @param relationKind 创建事务中的关系类型；用于输出可定位的失败警告。
     * @param definitionCommitMode 定义提交由本服务负责，还是由已存在的外层模型事务负责。
     * @returns 参数解写回成功，且需要时完成定义提交后返回 true。
     */
    public solveActiveSketchParametricRelations(
        seedObjects?: readonly THREE.Object3D[],
        relationKind?: SketchRelationKind,
        definitionCommitMode: 'service-managed' | 'parent-managed' = 'service-managed',
    ): boolean {
        const outcome = this.viewer.solveActiveSketchParametricState({
            ...(seedObjects && seedObjects.length > 0 ? {seedObjects} : {}),
            operationIntents: [],
            priorityAliases: new Map(),
            reason: 'parametric-relation-change',
        });

        if (!outcome.applied) {
            const build = outcome.solveResult?.build;
            const payload = {
                message: outcome.message ?? '统一参数系统未返回失败原因。',
                solveStatus: outcome.solveResult?.solveStatus ?? 'unknown',
                unsupportedConstraints: build?.unsupportedConstraints ?? [],
                unsupportedDimensions: build?.unsupportedDimensions ?? [],
                unsupportedOperationIntents: build?.unsupportedOperationIntents ?? [],
            };

            // 结构化诊断必须在创建服务边界完整保留，便于区分未迁移公式、无解和无活动草图。
            console.warn(
                relationKind ? `关系 ${relationKind} 无法由统一参数系统求解，已取消创建。` : '草图关系无法由统一参数系统求解，已取消当前变更。',
                payload,
            );
            return false;
        }

        if (definitionCommitMode === 'service-managed') {
            // 独立关系操作由服务提交定义；多边形、阵列等外层模型事务会在自身 after 边界统一提交。
            this.viewer.sketchGroupManager?.activeSketchGroup?.commitDefinitionChanges({
                reason: 'parametric-relation-change',
            });
        }
        return true;
    }

    /**
     * 捕获候选目标当前关系 ID，供统一求解失败时精确撤销新增关系。
     *
     * @param objects 候选关系目标对象。
     * @returns 对象运行时 ID 到既有关系 ID 集合。
     */
    private captureRelationIds(objects: readonly THREE.Object3D[]): ReadonlyMap<string, ReadonlySet<number>> {
        const snapshots = new Map<string, ReadonlySet<number>>();

        objects.forEach((object) => {
            const container = Reflect.get(object, 'constraints');
            const readAll = container ? Reflect.get(container, 'getRelations') : null;

            if (typeof readAll !== 'function') {
                return;
            }
            const ids = new Set<number>();
            const constraints = readAll.call(container);

            if (Array.isArray(constraints)) {
                constraints.forEach((constraint) => {
                    const id = Reflect.get(constraint as object, 'id');
                    if (typeof id === 'number') {
                        ids.add(id);
                    }
                });
            }
            snapshots.set(object.uuid, ids);
        });

        return snapshots;
    }

    /**
     * 删除本次候选绑定新增的关系，保证无历史运行时也不会残留半提交定义。
     *
     * @param objects 候选关系目标对象。
     * @param relationIdsBefore 创建前关系 ID 快照。
     */
    private rollbackCreatedRelations(
        objects: readonly THREE.Object3D[],
        relationIdsBefore: ReadonlyMap<string, ReadonlySet<number>>,
    ): void {
        objects.forEach((object) => {
            const container = Reflect.get(object, 'constraints');
            const readAll = container ? Reflect.get(container, 'getRelations') : null;
            const remove = container ? Reflect.get(container, 'removeRelation') : null;

            if (typeof readAll !== 'function' || typeof remove !== 'function') {
                return;
            }
            const existingIds = relationIdsBefore.get(object.uuid) ?? new Set<number>();
            const constraints = readAll.call(container);

            if (!Array.isArray(constraints)) {
                return;
            }
            constraints.forEach((constraint) => {
                const id = Reflect.get(constraint as object, 'id');
                if (typeof id === 'number' && !existingIds.has(id)) {
                    remove.call(container, id);
                }
            });
        });
    }

    /**
     * 创建 self 分类关系。
     * @param params 创建参数。
     * @returns 是否至少创建成功一条关系。
     */
    private createSelfRelation(params: GeometryRelationCreationParams): boolean {
        let created = false;


        /**
         * 使用运行时标记守卫代替 instanceof，
         * 以兼容跨包实例或代理对象场景。
         */
        params.objects.forEach((object) => {
            if (isSketchLineObject(object)) {
                created = this.tryCreateLineSelfRelation(object, params.relationKind) || created;
                return;
            }
            if (isSketchPointObject(object)) {
                created = this.tryCreatePointSelfRelation(object, params.relationKind) || created;
                return;
            }

            if (isSketchCircleObject(object)) {
                created = this.tryCreateCircleSelfRelation(object, params.relationKind) || created;
                return;
            }

            if (isSketchArcObject(object)) {
                created = this.tryCreateArcSelfRelation(object, params.relationKind) || created;
                return;
            }

            if (isSketchEllipseObject(object)) {
                created = this.tryCreateEllipseSelfRelation(object, params.relationKind) || created;
            }

        });


        return created;
    }

    /**
     * 创建 multi 分类关系。
     * @param params 创建参数。
     * @returns 是否创建成功。
     */
    /**
     * 创建槽口专属关系（`固定槽口` / `相等槽口`）。
     *
     * 说明：SOLIDWORKS 为槽口单列了这两个关系类型，约束直接挂在槽口实体上
     * （槽口自身持有约束容器，求解快照与持久化按鸭子类型自动接入）。
     *
     * @param params 创建参数。
     * @returns 是否创建成功。
     */
    private createSlotRelation(params: GeometryRelationCreationParams): boolean {
        const constraintClass = resolveSlotRelationConstraintClassMap()[params.relationKind];

        if (!constraintClass) {
            return false;
        }

        const slots = params.objects.filter((object) => Reflect.get(object, 'isSketchSlotObject') === true);
        const minimumCount = params.relationKind === SketchRelationKind.EqualSlot ? 2 : 1;

        if (slots.length < minimumCount) {
            return false;
        }

        const constraint = new constraintClass();

        slots.forEach((slot) => {
            const container = Reflect.get(slot, 'constraints') as {
                addRelation: (value: unknown) => void;
            } | undefined;

            container?.addRelation(constraint);
        });

        return true;
    }

    private createMultiRelation(params: GeometryRelationCreationParams): boolean {
        if (params.relationKind === SketchRelationKind.Tangent) {
            const selectedLineLikeObject = params.objects.find(
                (object): object is TangentLineLikeObject => this.isTangentLineLikeObject(object),
            );
            const selectedCurve = params.objects.find(
                (object): object is TangentCurveObject => this.isTangentCurveObject(object),
            );
            if (selectedLineLikeObject && selectedCurve) {
                const tangentConstraint = new TangentConstraint();
                selectedLineLikeObject.constraints.addRelation(
                    tangentConstraint as unknown as Parameters<typeof selectedLineLikeObject.constraints.addRelation>[0],
                );
                selectedCurve.constraints.addRelation(
                    tangentConstraint as unknown as Parameters<typeof selectedCurve.constraints.addRelation>[0],
                );
                return true;
            }

            /**
             * 圆、圆弧和椭圆的两两相切由同一个关系定义表达。
             * 具体内切/外切与接触分支由参数公式根据当前连续解选择，关系创建层不得再按对象类型拆求解器。
             */
            const selectedCurves = params.objects.filter(
                (object): object is TangentCurveObject => this.isTangentCurveObject(object),
            );
            if (selectedCurves.length === 2 && selectedCurves.length === params.objects.length) {
                const tangentConstraint = new TangentConstraint();
                selectedCurves.forEach((curve) => {
                    curve.constraints.addRelation(
                        tangentConstraint as unknown as Parameters<typeof curve.constraints.addRelation>[0],
                    );
                });
                return true;
            }
        }


        if (params.relationKind === SketchRelationKind.Midpoint) {
            const created = this.tryCreatePointLineMidpointRelation(params.objects);
            if (created) {
                return true;
            }
        }

        if (params.relationKind === SketchRelationKind.Symmetric) {
            return this.tryCreateEntitySymmetricRelation(params.objects);
        }
        if (params.relationKind === SketchRelationKind.Coincident) {
            const created = this.tryCreatePointCurveCoincidentRelation(params.objects);
            if (created) {
                return true;
            }
        }



        /**
         * 先读取线关系映射中的具体约束类。
         * 命中后创建单个多目标约束实例，并分别添加到每一条线。
         */
        const lineConstraintClass = resolveLineRelationConstraintClassMap()[params.relationKind];
        if (lineConstraintClass) {
            const selectedLines = params.objects.filter(
                (object): object is SketchLineObject => isSketchLineObject(object),
            );
            if (selectedLines.length <= 1) {
                return false;
            }

            const lineConstraint = new lineConstraintClass();
            selectedLines.forEach((line) => {
                line.constraints.addRelation(lineConstraint);
            });
            return true;
        }



        /**
         * 若线关系映射未命中，则尝试圆关系映射。
         * 命中后创建单个多目标约束实例，并分别添加到每一个圆。
         */
        const circleConstraintClass = resolveCircleRelationConstraintClassMap()[params.relationKind];
        if (circleConstraintClass) {
            const selectedCircles = params.objects.filter(
                (object): object is SketchCircleObject => isSketchCircleObject(object),
            );
            if (selectedCircles.length <= 1) {
                return false;
            }

            const circleConstraint = new circleConstraintClass();
            selectedCircles.forEach((circle) => {
                circle.constraints.addRelation(
                    circleConstraint as Parameters<typeof circle.constraints.addRelation>[0],
                );
            });
            return true;
        }



        /**
         * 若线关系映射未命中，则尝试点关系映射。
         * 命中后创建单个多目标约束实例，并分别添加到每一个点。
         */
        const pointConstraintClass = resolvePointRelationConstraintClassMap()[params.relationKind];
        if (!pointConstraintClass) {
            return false;
        }

        const selectedPoints = params.objects.filter(
            (object): object is SketchPointObject => isSketchPointObject(object),
        );
        if (selectedPoints.length <= 1) {
            return false;
        }

        const pointConstraint = new pointConstraintClass();
        selectedPoints.forEach((point) => {
            point.constraints.addRelation(
                pointConstraint as Parameters<typeof point.constraints.addRelation>[0],
            );
        });
        return true;

    }


    /**
     * 尝试创建点-线中点关系。
     * 说明：
     * 1. SolidWorks 的中点关系标准输入是“点 + 线”；
     * 2. 该组合不能走通用线关系分支，否则一条线会被判定为目标数量不足；
     * 3. 本方法只绑定候选定义，首次求解由 `createRelation` 的统一参数端口执行。
     * @param objects 当前选中对象集合。
     * @returns 创建成功返回 true。
     */
    private tryCreatePointLineMidpointRelation(objects: readonly THREE.Object3D[]): boolean {
        const selectedPoints = objects.filter(
            (object): object is SketchPointObject => isSketchPointObject(object),
        );
        const selectedLines = objects.filter(
            (object): object is SketchLineObject => isSketchLineObject(object),
        );

        if (selectedPoints.length !== 1 || selectedLines.length !== 1) {
            return false;
        }

        const midpointConstraint = new PointMidpointConstraint();
        const selectedPoint = selectedPoints[0];
        const selectedLine = selectedLines[0];

        selectedPoint.constraints.addRelation(midpointConstraint);
        selectedLine.constraints.addRelation(midpointConstraint);

        return true;
    }
    /**
     * 由一个镜像轴 provider 和两个同类草图实体创建实体级对称关系。
     *
     * 关系只绑定领域定义，候选求解、原子回滚和历史提交统一由 `createRelations` 负责；
     * 禁止在此重新调用对象式求解器或直接恢复几何定义。
     *
     * @param objects 当前选择对象。
     * @returns 选择精确匹配并成功创建关系时返回 true。
     */
    private tryCreateEntitySymmetricRelation(objects: readonly THREE.Object3D[]): boolean {
        if (objects.length !== 3) {
            return false;
        }

        /** 传统关系面板优先识别唯一中心线；镜像命令则按有序选择把轴放在最后。 */
        const centerlineAxes = objects.filter((object) => (
            isSketchLineObject(object) && object.isCenterline && isMirrorAxisGeometryProvider(object)
        ));
        const axisCandidate = centerlineAxes.length === 1 ? centerlineAxes[0] : objects.at(-1);

        if (!axisCandidate || !isMirrorAxisGeometryProvider(axisCandidate)) {
            return false;
        }

        const peers = objects.filter((object): object is THREE.Object3D & ISketchObject => (
            object !== axisCandidate && isSketchObject(object)
        ));
        const sourceEntity = peers[0];
        const mirroredEntity = peers[1];

        if (peers.length !== 2 || !sourceEntity || !mirroredEntity) {
            return false;
        }

        const constraint = new SketchEntitySymmetricConstraint(
            axisCandidate,
            sourceEntity,
            mirroredEntity,
        );

        [axisCandidate, sourceEntity, mirroredEntity].forEach((target) => {
            target.constraints.addRelation(constraint);
        });
        return true;
    }



    /**
     * 尝试创建点-曲线重合关系。
     * 说明：
     * 1. SolidWorks 的标准 Coincident 草图关系是“点 + 线/圆弧/圆/椭圆”；
     * 2. 该组合必须先于通用线/圆关系映射处理，避免一条曲线被误判为目标数量不足；
     * 3. 本方法只绑定候选定义，首次求解由 `createRelation` 的统一参数端口执行。
     * @param objects 当前选中对象集合。
     * @returns 创建成功返回 true。
     */
    private tryCreatePointCurveCoincidentRelation(objects: readonly THREE.Object3D[]): boolean {
        const selectedPoints = objects.filter(
            (object): object is SketchPointObject => isSketchPointObject(object),
        );
        const selectedCurves = objects.filter(
            (object): object is CoincidentCurveObject => this.isCoincidentCurveObject(object),
        );

        if (selectedPoints.length !== 1 || selectedCurves.length !== 1) {
            return false;
        }

        const coincidentConstraint = new PointCurveCoincidentConstraint();
        const selectedPoint = selectedPoints[0];
        const selectedCurve = selectedCurves[0];

        selectedPoint.constraints.addRelation(coincidentConstraint);
        selectedCurve.constraints.addRelation(coincidentConstraint);

        return true;
    }

    /**
     * 判断对象是否可作为 Coincident 关系中的曲线目标。
     * @param object 待判断对象。
     * @returns 是已参数化的平面曲线时返回 true。
     */
    private isCoincidentCurveObject(object: THREE.Object3D): object is CoincidentCurveObject {
        return isSketchLineObject(object)
            || isSketchCircleObject(object)
            || isSketchArcObject(object)
            || isSketchEllipseObject(object)
            || isSketchParabolaObject(object)
            || isSketchSplineObject(object)
            || isSketchStyleSplineObject(object);
    }


    /**
     * 判断对象是否可作为相切关系中的线性对象。
     * @param object 待判断对象。
     * @returns 是线/样条/样式样条返回 true。
     */
    private isTangentLineLikeObject(object: THREE.Object3D): object is TangentLineLikeObject {
        return isSketchLineObject(object)
            || isSketchSplineObject(object)
            || isSketchStyleSplineObject(object);
    }

    /**
     * 判断对象是否可作为相切关系中的曲线对象。
     * @param object 待判断对象。
     * @returns 是圆/圆弧/椭圆返回 true。
     */
    private isTangentCurveObject(object: THREE.Object3D): object is TangentCurveObject {
        return isSketchCircleObject(object) || isSketchArcObject(object) || isSketchEllipseObject(object);
    }

    /**
     * 尝试创建线 self 关系。
     * @param line 线对象。
     * @param kind 关系类型。
     * @returns 是否创建成功。
     */
    private tryCreateLineSelfRelation(line: SketchLineObject, kind: SketchRelationKind): boolean {
        const constraintClass = resolveLineRelationConstraintClassMap()[kind];
        if (!constraintClass) {
            return false;
        }

        const relationDefinition = new constraintClass();
        line.constraints.addRelation(relationDefinition);
        return true;
    }

    /**
     * 尝试创建点 self 关系。
     * @param point 点对象。
     * @param kind 关系类型。
     * @returns 是否创建成功。
     */
    private tryCreatePointSelfRelation(point: SketchPointObject, kind: SketchRelationKind): boolean {
        const constraintClass = resolvePointRelationConstraintClassMap()[kind];
        if (!constraintClass) {
            return false;
        }

        const relationDefinition = new constraintClass();
        point.constraints.addRelation(
            relationDefinition as Parameters<typeof point.constraints.addRelation>[0],
        );
        return true;
    }


    /**
     * 尝试创建圆 self 关系。
     * @param circle 圆对象。
     * @param kind 关系类型。
     * @returns 是否创建成功。
     */
    private tryCreateCircleSelfRelation(circle: SketchCircleObject, kind: SketchRelationKind): boolean {
        const constraintClass = resolveCircleRelationConstraintClassMap()[kind];
        if (!constraintClass) {
            return false;
        }

        const relationDefinition = new constraintClass();
        circle.constraints.addRelation(
            relationDefinition as Parameters<typeof circle.constraints.addRelation>[0],
        );
        return true;
    }

    /**
     * 尝试创建圆弧 self 关系。
     * @param arc 圆弧对象。
     * @param kind 关系类型。
     * @returns 是否创建成功。
     */
    private tryCreateArcSelfRelation(arc: SketchArcObject, kind: SketchRelationKind): boolean {
        // 圆弧使用专属 self 关系映射，避免误套用圆/椭圆约束实现。
        const constraintClass = resolveArcRelationConstraintClassMap()[kind];
        if (!constraintClass) {
            return false;
        }

        const relationDefinition = new constraintClass();
        arc.constraints.addRelation(
            relationDefinition as Parameters<typeof arc.constraints.addRelation>[0],
        );
        return true;
    }

    /**
     * 尝试创建椭圆 self 关系。
     * @param ellipse 椭圆对象。
     * @param kind 关系类型。
     * @returns 是否创建成功。
     */
    private tryCreateEllipseSelfRelation(ellipse: SketchEllipseObject, kind: SketchRelationKind): boolean {
        const constraintClass = resolveCircleRelationConstraintClassMap()[kind];
        if (!constraintClass) {
            return false;
        }

        const relationDefinition = new constraintClass();
        ellipse.constraints.addRelation(
            relationDefinition as Parameters<typeof ellipse.constraints.addRelation>[0],
        );
        return true;
    }


    /**
     * 构建线对象可用关系按钮列表。
     * @param line 目标线对象。
     * @returns 按钮视图模型数组。
     */
    private buildLineRelationButtons(line: SketchLineObject): AvailableRelationButtonViewModel[] {
        const hasHorizontal = line.constraints.hasType(SketchRelationKind.Horizontal);
        const hasVertical = line.constraints.hasType(SketchRelationKind.Vertical);
        const hasFix = line.constraints.hasType(SketchRelationKind.Fix);
        const lockByDirection = hasHorizontal || hasVertical;

        return [
            this.createButton(SketchRelationKind.Horizontal, '水平', hasHorizontal || lockByDirection),
            this.createButton(SketchRelationKind.Vertical, '竖直', hasVertical || lockByDirection),
            this.createButton(SketchRelationKind.Fix, '固定', hasFix),
        ];
    }

    /**
     * 构建圆对象可用关系按钮列表。
     * @param circle 目标圆对象。
     * @returns 按钮视图模型数组。
     */
    private buildCircleRelationButtons(circle: SketchCircleObject): AvailableRelationButtonViewModel[] {
        const hasFix = circle.constraints.hasType(SketchRelationKind.Fix);
        return [
            this.createButton(SketchRelationKind.Fix, '固定', hasFix),
        ];
    }

    /**
     * 构建圆弧对象可用关系按钮列表。
     * @param arc 目标圆弧对象。
     * @returns 按钮视图模型数组。
     */
    private buildArcRelationButtons(arc: SketchArcObject): AvailableRelationButtonViewModel[] {
        const hasFix = arc.constraints.hasType(SketchRelationKind.Fix);
        return [
            this.createButton(SketchRelationKind.Fix, '固定', hasFix),
        ];
    }

    /**
     * 构建椭圆对象可用关系按钮列表。
     * @param ellipse 目标椭圆对象。
     * @returns 按钮视图模型数组。
     */
    private buildEllipseRelationButtons(ellipse: SketchEllipseObject): AvailableRelationButtonViewModel[] {
        const hasFix = ellipse.constraints.hasType(SketchRelationKind.Fix);
        return [
            this.createButton(SketchRelationKind.Fix, '固定', hasFix),
        ];
    }

    /**
     * 生成可用关系按钮列表。
     * @param target 目标草图对象（当前支持线/圆/圆弧/椭圆）。
     * @returns 按钮视图模型数组。
     * @throws 当目标不是受支持的草图对象类型时抛出错误。
     */
    public build(target: SketchLineObject | SketchCircleObject | SketchArcObject | SketchEllipseObject): AvailableRelationButtonViewModel[] {
        if (isSketchLineObject(target)) {
            return this.buildLineRelationButtons(target);
        }
        if (isSketchCircleObject(target)) {
            return this.buildCircleRelationButtons(target);
        }
        if (isSketchArcObject(target)) {
            return this.buildArcRelationButtons(target);
        }
        if (isSketchEllipseObject(target)) {
            return this.buildEllipseRelationButtons(target);
        }
        throw new Error('未支持的几何关系按钮构建对象类型。');
    }

    /**
     * 构建单个关系按钮模型。
     * @param relationType 关系类型。
     * @param label 按钮文案。
     * @param disabled 是否禁用。
     * @returns 关系按钮视图模型。
     */
    private createButton(
        relationType: SketchRelationKind,
        label: string,
        disabled: boolean,
    ): AvailableRelationButtonViewModel {
        return {
            relationType,
            label,
            disabled,
        };
    }
}

